import { useEffect, useRef, useState } from 'react';
import {
  cancelSubscriptionWithoutRefund,
  cancelSubscriptionWithCustomRefund,
} from 'shared/services';

interface UseSubscriptionActionsProps {
  subscriptionId: string;
  uid: string;
  userImpersonationId: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const useSubscriptionActions = ({
  subscriptionId,
  uid,
  userImpersonationId,
  onSuccess,
  onError,
}: UseSubscriptionActionsProps) => {
  const isMountedRef = useRef(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showWithoutRefundModal, setShowWithoutRefundModal] = useState(false);
  const [showCustomRefundModal, setShowCustomRefundModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    type: 'withoutRefund' | 'customRefund';
    amount?: number;
    isAutomatic?: boolean;
  } | null>(null);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleWithoutRefundClick = () => {
    setShowWithoutRefundModal(true);
  };

  const handleCustomRefundClick = () => {
    setShowCustomRefundModal(true);
  };

  const handleCustomRefundConfirm = (amount: number, isAutomatic: boolean) => {
    setConfirmationData({ type: 'customRefund', amount, isAutomatic });
    setShowCustomRefundModal(false);
    setShowConfirmationModal(true);
  };

  const handleWithoutRefundConfirm = () => {
    setConfirmationData({ type: 'withoutRefund' });
    setShowWithoutRefundModal(false);
    setShowConfirmationModal(true);
  };

  const executeCancellation = async () => {
    if (!confirmationData) return;

    setIsLoading(true);
    try {
      let response;

      if (confirmationData.type === 'withoutRefund') {
        response = await cancelSubscriptionWithoutRefund(
          subscriptionId,
          uid,
          userImpersonationId
        );
      } else {
        response = await cancelSubscriptionWithCustomRefund(
          subscriptionId,
          uid,
          userImpersonationId,
          confirmationData.amount || 0,
          confirmationData.isAutomatic || false
        );
      }

      if (!isMountedRef.current) return;

      if (response.success) {
        setShowConfirmationModal(false);
        setConfirmationData(null);
        onSuccess?.();
      } else {
        onError?.(response.message);
      }
    } catch (error) {
      if (!isMountedRef.current) return;
      const errorMessage =
        'Error al cancelar la suscripción. Por favor intenta nuevamente.';
      onError?.(errorMessage);
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  };

  const closeAllModals = () => {
    setShowWithoutRefundModal(false);
    setShowCustomRefundModal(false);
    setShowConfirmationModal(false);
    setConfirmationData(null);
  };

  return {
    isLoading,
    showWithoutRefundModal,
    showCustomRefundModal,
    showConfirmationModal,
    confirmationData,
    handleWithoutRefundClick,
    handleCustomRefundClick,
    handleCustomRefundConfirm,
    handleWithoutRefundConfirm,
    executeCancellation,
    closeAllModals,
  };
};
